// Helper function to convert WebM/Opus audio blob to WAV format
// This is needed because POC expects WAV format, but browsers record in WebM
export const convertWebMToWav = async (webmBlob: Blob): Promise<Blob> => {
  try {
    // Create AudioContext to decode the WebM audio
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Convert blob to ArrayBuffer
    const arrayBuffer = await webmBlob.arrayBuffer();

    // Decode the audio data to get raw PCM
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Get audio parameters
    const sampleRate = audioBuffer.sampleRate;
    const numberOfChannels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;

    // Convert Float32Array to Int16Array (16-bit PCM)
    const pcmData = new Int16Array(length * numberOfChannels);
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        // Convert float (-1.0 to 1.0) to 16-bit integer (-32768 to 32767)
        const sample = Math.max(-1, Math.min(1, channelData[i]));
        pcmData[i * numberOfChannels + channel] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      }
    }

    // Create WAV file header
    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);

    // RIFF header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + pcmData.length * 2, true); // File size - 8
    writeString(8, "WAVE");

    // fmt chunk
    writeString(12, "fmt ");
    view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
    view.setUint16(20, 1, true); // AudioFormat (1 = PCM)
    view.setUint16(22, numberOfChannels, true); // NumChannels
    view.setUint32(24, sampleRate, true); // SampleRate
    view.setUint32(28, sampleRate * numberOfChannels * 2, true); // ByteRate
    view.setUint16(32, numberOfChannels * 2, true); // BlockAlign
    view.setUint16(34, 16, true); // BitsPerSample

    // data chunk
    writeString(36, "data");
    view.setUint32(40, pcmData.length * 2, true); // Subchunk2Size

    // Combine header and PCM data
    const wavBlob = new Blob([wavHeader, pcmData.buffer], { type: "audio/wav" });

    // Close AudioContext to free resources
    await audioContext.close();

    console.log(`Converted WebM (${webmBlob.size} bytes) to WAV (${wavBlob.size} bytes)`);
    return wavBlob;
  } catch (error) {
    console.error("Error converting WebM to WAV:", error);
    throw error;
  }
};
